import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ofType, Epic, combineEpics } from 'redux-observable';
import { of, from } from 'rxjs';
import { switchMap, map, catchError, takeUntil, mapTo } from 'rxjs/operators';

import { apolloClient } from 'api/apollo';
import { LoginEmailDocument, MutationLoginEmailArgs, User } from 'api/graphql';
import { RootState } from 'redux/reducers';
import { setLocation } from 'redux/router';

type AuthAction = {
  type: string;
  payload?: any;
};

type AuthState = {
  token?: string | null;
  user?: User | null;
  loading: boolean;
  error: boolean;
};

export type AuthRootState = {
  auth: AuthState;
};

// Selectors
export const getUser = ({ auth }: AuthRootState) => auth.user;
export const getRole = ({ auth }: AuthRootState) => auth.user?.role;
export const getToken = ({ auth }: AuthRootState) => auth.token;
export const getError = ({ auth }: AuthRootState) => auth.error;
export const getLoggedIn = ({ auth }: AuthRootState) => !!auth.token;
export const getLoading = ({ auth }: AuthRootState) => auth.loading;

// Reducers
const initialState: AuthState = {
  token: null,
  user: null,
  loading: false,
  error: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state, _action: PayloadAction<MutationLoginEmailArgs>) => {
      state.token = null;
      state.user = null;
      state.loading = true;
      state.error = false;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{ token: string; user: User }>
    ) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.loading = false;
      state.error = false;
    },
    loginError: (state) => {
      state.token = null;
      state.user = null;
      state.loading = false;
      state.error = true;
    },
    logoutStart: (state) => {
      state.loading = true;
    },
    logoutSuccess: (state) => {
      state.token = null;
      state.user = null;
      state.loading = false;
      state.error = false;
    },
    setUser: (state, action: PayloadAction<{ user: User }>) => {
      state.user = action.payload.user;
    },
    setError: (state, action: PayloadAction<boolean>) => {
      state.error = action.payload;
    },
    setToken: (state, action: PayloadAction<{ token: string }>) => {
      state.token = action.payload.token;
    },
  },
});

export default authSlice.reducer;

// Actions
export const {
  loginStart,
  loginSuccess,
  loginError,
  logoutStart,
  logoutSuccess,
  setUser,
  setError,
  setToken,
} = authSlice.actions;

// Epics
const loginEpic: Epic<AuthAction, AuthAction, RootState> = (action$) =>
  action$.pipe(
    ofType(loginStart.type),
    switchMap(({ payload: { email, password } }) =>
      from(
        apolloClient.mutate({
          mutation: LoginEmailDocument,
          variables: { email, password },
        })
      ).pipe(
        map(({ data }) => {
          const { success, jwt, user } = data.loginEmail;

          if (success) {
            return loginSuccess({
              token: jwt,
              user,
            });
          }

          return loginError();
        }),
        takeUntil(action$.pipe(ofType(setLocation.type))),
        catchError(() => of(loginError()))
      )
    )
  );

const logoutEpic: Epic<AuthAction, AuthAction, RootState> = (action$) =>
  action$.pipe(ofType(logoutStart.type), mapTo(logoutSuccess()));

export const authEpics = combineEpics(loginEpic, logoutEpic);
